import {
  useAppointmentControllerServiceGetAllAppointmentsKey,
  useDashboardControllerServiceGetLabAndImagingResultsKey,
  useDashboardControllerServiceGetMessegeInfoKey,
  useDashboardControllerServiceGetPendingItemsKey,
  useHAndPNoteControllerServiceGetAllEncounterKey,
  useHAndPNoteControllerServiceGetUnsignedNotesByUserKey,
  useLabOrderControllerServiceGetAllDraftLabAndImagingOrdersKey,
  useMessageControllerServiceGetMessageSummariesByUserUuidKey,
  useMessageControllerServiceGetUrgentUnreadMessageCountForUserKey,
  useMessageControllerServiceGetUrgentUnreadMessagesForUserKey,
  useTaskControllerServiceGetAllTasksKey,
} from "../services/queries";

/**
 * Mirrors the backend `RealtimeEntity` enum. The server tags every realtime
 * push with one of these, so the dashboard can tell which widget the change
 * belongs to without needing a distinct socket event name per entity.
 */
export const REALTIME_ENTITIES = [
  "APPOINTMENT",
  "TASK",
  "ERX_REQUEST",
  "VISIT_NOTE",
  "LAB_ORDER",
  "LAB_RESULT",
  "IMAGING_ORDER",
  "IMAGING_RESULT",
  "POC",
  "QUESTIONNAIRE",
  "PENDING_ITEM",
  "REFERRAL",
  "DOCUMENT",
  "COMMENT",
] as const;

export type RealtimeEntity = (typeof REALTIME_ENTITIES)[number];

/**
 * The appointments API behind the scheduling calendar view, the scheduling
 * list view's Appointments tab, and the dashboard's appointment widget — all
 * three read `getAllAppointments`, so one key refreshes every one of them.
 */
export const APPOINTMENT_QUERY_KEYS: string[] = [
  useAppointmentControllerServiceGetAllAppointmentsKey,
];

/**
 * The encounter feed behind the scheduling list view's Unsigned and Signed
 * Visit Notes tabs. Both tabs hit `getAllEncounter` and differ only by the
 * `signed` flag, so one key covers the pair.
 *
 * Moving an appointment's status is what opens and closes those encounters —
 * checking a patient in creates the visit, completing it makes the note
 * signable — so an appointment status push moves these tabs too. No screen
 * outside the scheduling list view reads this API, so refreshing it on an
 * appointment event costs nothing elsewhere.
 */
export const ENCOUNTER_QUERY_KEYS: string[] = [
  useHAndPNoteControllerServiceGetAllEncounterKey,
];

/**
 * Everything the scheduling screen shows: the calendar and the Appointments
 * tab (appointments), plus the Unsigned / Signed Visit Notes tabs
 * (encounters). An appointment realtime push refreshes all of it.
 */
export const SCHEDULING_QUERY_KEYS: string[] = [
  ...APPOINTMENT_QUERY_KEYS,
  ...ENCOUNTER_QUERY_KEYS,
];

/**
 * The two APIs behind the KPI row. `getMessegeInfo` carries every tile except
 * the urgent-message one, so any entity change can move a count — these are
 * refreshed for *every* realtime event regardless of entity.
 */
export const DASHBOARD_COUNT_QUERY_KEYS: string[] = [
  useDashboardControllerServiceGetMessegeInfoKey,
  useMessageControllerServiceGetUrgentUnreadMessageCountForUserKey,
];

/**
 * The list/detail query each entity feeds, on top of the count keys above.
 *
 * Several entities collapse onto one API: lab and imaging *orders* are both
 * served by `getAllDraftLabAndImagingOrders`, and lab/imaging/POC/document
 * *results* are all served by `getLabAndImagingResults`. `getPendingItems` is
 * a cross-cutting feed that surfaces tasks, orders, results, encounters and
 * referrals together, so most entities invalidate it as well.
 *
 * Two entities map to nothing:
 *  - ERX_REQUEST — the widget is a ScriptSure iframe, not a React Query list.
 *    Reloading it would discard in-progress prescribing, so only the KPI count
 *    updates.
 *  - QUESTIONNAIRE — no dashboard widget or tile exists for it today.
 *
 * COMMENT is a message posted on a task's thread. It only moves the task's
 * `commentCount` badge, which both the task list and the pending-items feed
 * carry.
 */
export const ENTITY_QUERY_KEYS: Record<RealtimeEntity, string[]> = {
  APPOINTMENT: APPOINTMENT_QUERY_KEYS,
  // Task and note activity can change which results are still awaiting
  // review, so both refresh the results card too.
  TASK: [
    useTaskControllerServiceGetAllTasksKey,
    useDashboardControllerServiceGetPendingItemsKey,
    useDashboardControllerServiceGetLabAndImagingResultsKey,
  ],
  ERX_REQUEST: [],
  VISIT_NOTE: [
    useHAndPNoteControllerServiceGetUnsignedNotesByUserKey,
    useDashboardControllerServiceGetPendingItemsKey,
    useDashboardControllerServiceGetLabAndImagingResultsKey,
  ],
  LAB_ORDER: [
    useLabOrderControllerServiceGetAllDraftLabAndImagingOrdersKey,
    useDashboardControllerServiceGetPendingItemsKey,
  ],
  IMAGING_ORDER: [
    useLabOrderControllerServiceGetAllDraftLabAndImagingOrdersKey,
    useDashboardControllerServiceGetPendingItemsKey,
  ],
  LAB_RESULT: [
    useDashboardControllerServiceGetLabAndImagingResultsKey,
    useDashboardControllerServiceGetPendingItemsKey,
  ],
  IMAGING_RESULT: [
    useDashboardControllerServiceGetLabAndImagingResultsKey,
    useDashboardControllerServiceGetPendingItemsKey,
  ],
  POC: [
    useDashboardControllerServiceGetLabAndImagingResultsKey,
    useDashboardControllerServiceGetPendingItemsKey,
  ],
  DOCUMENT: [
    useDashboardControllerServiceGetLabAndImagingResultsKey,
    useDashboardControllerServiceGetPendingItemsKey,
  ],
  REFERRAL: [useDashboardControllerServiceGetPendingItemsKey],
  PENDING_ITEM: [useDashboardControllerServiceGetPendingItemsKey],
  QUESTIONNAIRE: [],
  COMMENT: [
    useTaskControllerServiceGetAllTasksKey,
    useDashboardControllerServiceGetPendingItemsKey,
  ],
};

/**
 * Query keys refreshed by the message-shaped socket events
 * (`receive_message`, `thread_list_updated`, `message_summary_updated`).
 * These pre-date the entity protocol and stay on their own path because they
 * carry the open-thread deferral rules the entity events do not need.
 */
export const MESSAGE_QUERY_KEYS: string[] = [
  useMessageControllerServiceGetUrgentUnreadMessagesForUserKey,
  useMessageControllerServiceGetMessageSummariesByUserUuidKey,
  useDashboardControllerServiceGetPendingItemsKey,
];

const ENTITY_LOOKUP = new Set<string>(REALTIME_ENTITIES);

/** Narrows an arbitrary socket payload field to a known entity. */
export const isRealtimeEntity = (value: unknown): value is RealtimeEntity =>
  typeof value === "string" && ENTITY_LOOKUP.has(value);

/**
 * The entities whose pushes invalidate `queryKey` — the inverse of
 * `ENTITY_QUERY_KEYS`, so a card reacts to exactly the events that refetch it.
 */
export const entitiesForQueryKey = (queryKey: string): RealtimeEntity[] =>
  REALTIME_ENTITIES.filter((entity) =>
    ENTITY_QUERY_KEYS[entity].includes(queryKey),
  );
